import React, { useState, useEffect, useMemo, useRef } from 'react';
import './ScheduledInterview.css';

// Minimal Calendly-like scheduler for admin: select dates on calendar and choose 1-hour slots.
// Props:
// - initialAvailability: array of { date: 'YYYY-MM-DD', times: ['09:00', ...] }
// - onSave: function(availabilityJSON)

// Business hours: start at 8:00, last slot starts at 16:00 (ends 17:00)
const BUSINESS_START = 8; // 8 AM
const BUSINESS_END_EXCLUSIVE = 17; // exclusive end (17 -> last start is 16)
const HOURS = Array.from({ length: BUSINESS_END_EXCLUSIVE - BUSINESS_START }, (_, i) => BUSINESS_START + i)
  .map((h) => `${String(h).padStart(2, '0')}:00`);

function formatHourStandard(hhmm) {
  // hhmm like '08:00' -> '8:00 AM'
  const [hh, mm] = hhmm.split(':').map(Number);
  const period = hh >= 12 ? 'PM' : 'AM';
  const hour12 = ((hh + 11) % 12) + 1; // converts 0->12, 13->1, etc.
  return `${hour12}:${String(mm).padStart(2, '0')} ${period}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getMonthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay(); // 0-6 Sun-Sat
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const matrix = [];
  let week = new Array(7).fill(null);
  let day = 1;
  for (let i = 0; i < startDay; i++) week[i] = null;
  for (let i = startDay; i < 7; i++) {
    week[i] = day++;
  }
  matrix.push(week);
  while (day <= daysInMonth) {
    week = new Array(7).fill(null);
    for (let i = 0; i < 7 && day <= daysInMonth; i++) {
      week[i] = day++;
    }
    matrix.push(week);
  }
  return matrix;
}

function formatDateYMD(year, monthIndex, day) {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function ScheduledInterview({ initialAvailability = [], onSave }) {
  const today = new Date();
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState(null); // 'YYYY-MM-DD'
  const [availability, setAvailability] = useState({}); // { 'YYYY-MM-DD': Set(['09:00', ...]) }

  // Guarded initializer: keep a ref to the serialized initialAvailability so
  // we don't re-run state updates if the parent passes a new array/object
  // reference on every render (common cause of "Maximum update depth exceeded").
  const _initialRef = useRef(null);
  useEffect(() => {
    try {
      const serialized = JSON.stringify(initialAvailability || []);
      if (_initialRef.current === serialized) return; // nothing changed
      _initialRef.current = serialized;
    } catch (e) {
      // fallback if circular structure (unlikely) — still proceed
    }

    // load initialAvailability
    const obj = {};
    (initialAvailability || []).forEach((item) => {
      obj[item.date] = new Set(item.times || []);
    });
    setAvailability(obj);
    if (initialAvailability && initialAvailability.length) {
      setSelectedDate(initialAvailability[0].date);
      const [y, m, d] = initialAvailability[0].date.split('-').map(Number);
      setCursor(startOfMonth(new Date(y, m - 1, d)));
    }
  }, [initialAvailability]);

  const monthMatrix = useMemo(() => getMonthMatrix(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  function toggleHourForDate(date, hour) {
    setAvailability((prev) => {
      const copy = { ...prev };
      const setForDate = new Set(copy[date] ? Array.from(copy[date]) : []);
      if (setForDate.has(hour)) setForDate.delete(hour);
      else setForDate.add(hour);
      if (setForDate.size === 0) delete copy[date];
      else copy[date] = setForDate;
      return copy;
    });
  }

  function handleDayClick(day) {
    if (!day) return;
    const weekday = new Date(cursor.getFullYear(), cursor.getMonth(), day).getDay();
    // disable weekends (0 = Sun, 6 = Sat)
    if (weekday === 0 || weekday === 6) return;
    const d = formatDateYMD(cursor.getFullYear(), cursor.getMonth(), day);
    setSelectedDate(d);
  }

  function save() {
    const result = Object.keys(availability).map((date) => ({ date, times: Array.from(availability[date]).sort() }));
    if (onSave) onSave(result);
    else {
      // fallback: log to console and return result
      console.log('Saved availability:', JSON.stringify(result, null, 2));
      return result;
    }
  }

  function goPrevMonth() {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  }
  function goNextMonth() {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  }

  const selectedSet = availability[selectedDate] || new Set();

  return (
    <div className="si-root">
      <div className="si-calendar">
        <div className="si-cal-header">
          <button onClick={goPrevMonth} className="si-nav">‹</button>
          <div className="si-month-label">{cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</div>
          <button onClick={goNextMonth} className="si-nav">›</button>
        </div>
        <div className="si-weekdays">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
            <div key={d} className="si-weekday">{d}</div>
          ))}
        </div>
        <div className="si-days">
          {monthMatrix.map((week, wi) => (
            <div className="si-week" key={wi}>
              {week.map((day, di) => {
                const dateStr = day ? formatDateYMD(cursor.getFullYear(), cursor.getMonth(), day) : null;
                const hasSlots = dateStr && availability[dateStr] && availability[dateStr].size > 0;
                const isSelected = dateStr === selectedDate;
                const isToday = dateStr === formatDateYMD(today.getFullYear(), today.getMonth(), today.getDate());
                return (
                  <div
                    key={di}
                    className={`si-day ${day ? '' : 'si-day-empty'} ${isSelected ? 'si-day-selected' : ''} ${hasSlots ? 'si-day-hasSlots' : ''} ${isToday ? 'si-day-today' : ''}`}
                    onClick={() => handleDayClick(day)}
                  >
                    {day || ''}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="si-side">
        <div className="si-side-header">Time slots for {selectedDate || '— select a date —'}</div>
        <div className="si-slots">
          {selectedDate ? (
            HOURS.map((h) => (
              <label key={h} className={`si-slot ${selectedSet.has(h) ? 'si-slot-selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedSet.has(h)}
                  onChange={() => toggleHourForDate(selectedDate, h)}
                />
                <span className="si-slot-label">{formatHourStandard(h)}</span>
              </label>
            ))
          ) : (
            <div className="si-empty">Pick a weekday (Mon–Fri) on the calendar to choose hourly slots (8:00 AM–5:00 PM).</div>
          )}
        </div>

        <div className="si-actions">
          <button className="si-save" onClick={() => save()}>Save availability</button>
        </div>
      </div>
    </div>
  );
}
